import moment from 'moment'
import objectPath from 'object-path'
import {
  calDate,
  calDay,
  calMonth,
  DEFAULT_DATE,
  DEFAULT_DATETIME,
  DEFAULT_TIME,
  getDateTimeFormValue,
} from './Date'
import { Col, Form, Row } from 'react-bootstrap'
import parse from 'html-react-parser'
import { isEmpty, isExclude, isInclude } from './condition/State'
import { CONFIG_NAME } from './Config'
import cloneDeep from 'clone-deep'

export const getUrlParamValue = (urlParams = {}, paramName, fallbackKey) => {
  let paramKey = paramName || fallbackKey
  let value = objectPath.get(urlParams, paramKey)
  if (value === undefined && paramKey) {
    value = urlParams[paramKey]
  }
  if (value === undefined && typeof paramName === 'string') {
    let keyList = paramName.split('.')
    let lastKey = keyList[keyList.length - 1]
    value = objectPath.get(urlParams, lastKey)
    if (value === undefined) {
      value = urlParams[lastKey]
    }
  }
  if (value === undefined && fallbackKey) {
    value = objectPath.get(urlParams, fallbackKey)
    if (value === undefined) {
      value = urlParams[fallbackKey]
    }
  }
  return value
}

export const checkDefaultValueByConfig = (
  defaultValue,
  key,
  auth = {},
  formCode,
) => {
  let value = defaultValue
  const { config = [] } = auth || {}
  const defaultValueConfig = Array.isArray(config)
    ? config.find(({ configCode }) => configCode === CONFIG_NAME.defaultValue)
    : null
  if (defaultValueConfig && key) {
    const hasDefaultFromConfig = objectPath.has(defaultValueConfig, [
      'jsonData',
      formCode,
      key,
    ])
    if (hasDefaultFromConfig) {
      value = objectPath.get(defaultValueConfig, ['jsonData', formCode, key])
    }
  }
  return value
}

export const hasDefaultValueConfig = (key, auth = {}, formCode) => {
  const { config = [] } = auth || {}
  const defaultValueConfig = Array.isArray(config)
    ? config.find(({ configCode }) => configCode === CONFIG_NAME.defaultValue)
    : null

  return (
    defaultValueConfig &&
    key &&
    objectPath.has(defaultValueConfig, ['jsonData', formCode, key])
  )
}

const getDefaultValueByDefaultType = (defaultValues, ui = {}) => {
  let defaultType = objectPath.get(ui, 'defaultType')
  let defaultValue = {
    ...defaultValues,
    custom: objectPath.get(ui, 'defaultValue'),
  }
  if (
    defaultType !== undefined &&
    defaultType !== null &&
    objectPath.has(defaultValue, defaultType)
  ) {
    return objectPath.get(defaultValue, defaultType)
  }
  return objectPath.get(ui, 'defaultValue')
}

const getDefaultDateTimeValueByDefaultType = (ui = {}) => {
  let defaultType = objectPath.get(ui, 'defaultType')
  let defaultValue = {
    ...DEFAULT_DATETIME,
    current: getDateTimeFormValue(new Date(), ui?.timestamp),
    custom: getDateTimeFormValue(
      objectPath.get(ui, 'defaultValue'),
      ui?.timestamp,
    ),
  }
  if (
    defaultType !== undefined &&
    defaultType !== null &&
    objectPath.has(defaultValue, defaultType)
  ) {
    return objectPath.get(defaultValue, defaultType)
  }
  return objectPath.get(ui, 'defaultValue')
}

const getDefaultValueByCheckbox = (ui = {}) => {
  let valueChecked = objectPath.has(ui, 'valueChecked')
    ? getCheckValue(ui.valueChecked)
    : true
  let valueUnchecked = objectPath.has(ui, 'valueUnchecked')
    ? getCheckValue(ui.valueUnchecked)
    : false
  return getCheckValue(ui.defaultChecked) ? valueChecked : valueUnchecked
}

const getDefaultValueByUi = (ui = {}, defaultValueKey) => {
  const isCheckbox = ['checkbox', 'checkboxInput'].includes(ui.tagname)
  const hasCheckboxValue =
    objectPath.has(ui, 'defaultChecked') ||
    objectPath.has(ui, 'valueChecked') ||
    objectPath.has(ui, 'valueUnchecked')
  if (defaultValueKey) {
    if (defaultValueKey === 'defaultValue') {
      if (ui.tagname === 'date') {
        return getDefaultValueByDefaultType(DEFAULT_DATE, ui)
      } else if (ui.tagname === 'time') {
        return getDefaultValueByDefaultType(DEFAULT_TIME, ui)
      } else if (ui.tagname === 'dateTime' || ui.tagname === 'datetime') {
        return getDefaultDateTimeValueByDefaultType(ui)
      }
    }
    return objectPath.get(ui, defaultValueKey)
  }
  if (ui.tagname === 'date') {
    return getDefaultValueByDefaultType(DEFAULT_DATE, ui)
  } else if (ui.tagname === 'time') {
    return getDefaultValueByDefaultType(DEFAULT_TIME, ui)
  } else if (ui.tagname === 'dateTime' || ui.tagname === 'datetime') {
    return getDefaultDateTimeValueByDefaultType(ui)
  } else if (isCheckbox && hasCheckboxValue) {
    return getDefaultValueByCheckbox(ui)
  } else if (objectPath.has(ui, 'defaultValue')) {
    return objectPath.get(ui, 'defaultValue')
  } else if (objectPath.has(ui, 'defaultChecked')) {
    return objectPath.get(ui, 'defaultChecked')
  } else if (
    ui.tagname !== 'checkboxInput' &&
    objectPath.has(ui, 'input.defaultValue')
  ) {
    return objectPath.get(ui, 'input.defaultValue')
  }
}

export const getDefaultValueFromUi = (
  ui = {},
  key,
  jsonForm = {},
  auth = {},
  defaultValueKey,
) => {
  let defaultValue = getDefaultValueByUi(ui, defaultValueKey)
  return checkDefaultValueByConfig(defaultValue, key, auth, jsonForm.formCode)
}

const getValueFromInternalSchema = (internalSchema, paramName) => {
  let value = null
  if (internalSchema) {
    let valueType = objectPath.get(internalSchema, `${paramName}.type`)
    if (valueType === 'string') {
      value = ''
    } else if (valueType === 'array') {
      value = []
    } else if (valueType === 'object') {
      value = {}
    }
  }
  return value
}

export const updateMapFormData = ({
  form,
  source,
  destination = {},
  urlParams,
  isRequired,
  setDataByKey = true,
  setDataByName = true,
  internalSchema = null,
  jsonForm = {},
}) => {
  if (!form) return destination
  let { formComponentName, jsonKey = '', mapParam = {}, mapOther = {} } = form
  let paramList = Object.keys(mapParam)

  paramList.forEach((paramKey) => {
    let ui = getCurrentUi(paramKey, jsonForm.schema, jsonForm.uischema)
    let paramName
    if (ui.key === paramKey) {
      paramName = ui.name
    } else {
      paramName = mapParam[paramKey]
    }
    let dataKey = jsonKey ? `${jsonKey}.${paramName}` : paramName

    let value
    if (objectPath.has(source, paramKey)) {
      value = objectPath.get(source, paramKey)
    } else if (objectPath.has(source, dataKey)) {
      value = objectPath.get(source, dataKey)
    }

    if (ui.tagname === 'panelGroup') {
      let panelGroup = ui.elements
      panelGroup.forEach((panel) => {
        let panelName = panel.name
        let subPanels = panel.elements

        subPanels.forEach((subPanel) => {
          let subPanelName = subPanel.name
          if (subPanel.tagname === 'checkboxGroup') {
            let checkboxValueList = objectPath.get(source, subPanelName)
            let checkboxOptions = subPanel.options || []
            if (subPanel.valueType === 'text') {
              let joinValue = subPanel.joinValue || ''
              let splitValue = checkboxValueList.split(joinValue)
              splitValue.forEach((checkboxValue) => {
                let foundSplitValue = checkboxOptions.find(
                  ({ name }) => name === checkboxValue,
                )
                let checked = foundSplitValue ? true : false
                if (checked) {
                  objectPath.set(
                    destination,
                    `@${panelName}.${checkboxValue}`,
                    checked,
                  )
                  objectPath.set(
                    destination,
                    `@${panelName}.${subPanelName}`,
                    checkboxValueList,
                  )
                }
              })
              let isCheckAll = splitValue.length === checkboxOptions.length
              objectPath.set(
                destination,
                `@isCheckedAll.@${panelName}.${subPanelName}`,
                isCheckAll,
              )
            } else {
              checkboxOptions.forEach(({ name }) => {
                let checked
                if (objectPath.get(checkboxValueList, name)) {
                  checked = objectPath.get(checkboxValueList, name)
                } else if (objectPath.get(source, name)) {
                  checked = objectPath.get(source, name)
                }
                objectPath.set(destination, `@${panelName}.${name}`, checked)
              })
            }
          } else {
            let value = objectPath.get(source, subPanelName)
            objectPath.set(destination, `@${panelName}.${subPanelName}`, value)
          }
        })
      })
    }
    if (formComponentName) {
      paramKey = `${formComponentName}.${paramKey}`
      dataKey = `${formComponentName}.${dataKey}`
    }
    if (value !== undefined) {
      if (setDataByKey) objectPath.set(destination, paramKey, value)
      if (setDataByName) objectPath.set(destination, dataKey, value)
    } else {
      if (isRequired) {
        let value = internalSchema
          ? getValueFromInternalSchema(internalSchema, paramName)
          : undefined
        if (setDataByKey) objectPath.set(destination, paramKey, value)
        if (setDataByName) objectPath.set(destination, dataKey, value)
      }
    }
  })
  let otherList = Object.values(mapOther)
  otherList.forEach(({ name, value, dataType = '' }) => {
    let dataKey = jsonKey ? `${jsonKey}.${name}` : name
    if (dataType === 'prop') {
      value = objectPath.get(source, name)
    } else if (dataType === 'urlParam') {
      value = getUrlParamValue(urlParams, value, dataKey)
    }
    if (formComponentName) {
      dataKey = `${formComponentName}.${dataKey}`
    }
    if (value !== undefined) {
      objectPath.set(destination, dataKey, value)
    } else {
      if (isRequired) {
        let value = internalSchema
          ? getValueFromInternalSchema(internalSchema, name)
          : undefined
        objectPath.set(destination, dataKey, value)
      }
    }
  })
  return destination
}

export const updateInputValue = ({
  destination,
  key,
  name,
  value,
  type,
  isRequired = true,
}) => {
  if (value === undefined) {
    if (type === 'url-param') return destination
    if (isRequired) {
      objectPath.set(destination, key, '')
      objectPath.set(destination, name, '')
    }
  } else {
    objectPath.set(destination, key, value)
    objectPath.set(destination, name, value)
  }
  return destination
}

export const updateFormDataValue = ({
  jsonForm,
  formGroup,
  formDataName,
  formDataKey,
  source,
  destination,
  urlParams = {},
  isRequired = true,
  internalSchema = null,
}) => {
  let form = formGroup.find(
    (group) => group.key === formDataKey || group.formDataName === formDataName,
  )

  let { isGroup = false, groupData = [] } = form || {}
  if (isGroup) {
    groupData.forEach((form) => {
      destination = updateMapFormData({
        jsonForm,
        form: form,
        source: source,
        destination: destination,
        urlParams: urlParams,
        isRequired: isRequired,
        internalSchema: internalSchema,
      })
    })
  } else {
    destination = updateMapFormData({
      jsonForm,
      form: form,
      source: source,
      destination: destination,
      urlParams: urlParams,
      isRequired: isRequired,
      internalSchema: internalSchema,
    })
  }
  return destination
}

export const getResetJsonForm = (formCode, perspectiveRoute) => {
  let form = localStorage.getItem(formCode)
  let jsonForm = JSON.parse(form)
  let formDesign = {
    formModuleId: jsonForm?.formModuleId || null,
    formDesignId: jsonForm?.formDesignId || null,
    formRevisionId: jsonForm?.formRevisionId || null,
  }
  return {
    formDesign: formDesign,
    jsonForm: jsonForm,
    perspectiveRoute: perspectiveRoute || '',
  }
}

export const generateString = (length) => {
  const characters =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
  let result = ''
  const charactersLength = characters.length
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength))
  }

  return result
}

export const getAllFormGroup = (jsonForm, widgetDesign) => {
  let props = objectPath.get(jsonForm, 'designerPropsData.props') || []
  let formGroup = jsonForm?.formGroup || []
  let internalGroup = formGroup.filter(({ isSystem }) => isSystem)
  let externalGroup = props.filter(({ propType }) => propType === 'formData')
  let allFormGroup = internalGroup.map((item) => {
    item.isSystem = true
    return item
  })
  externalGroup.forEach(
    ({
      prop,
      systemKey,
      fullPropKey,
      systemName,
      system = [],
      isDynamic,
      elementKey,
    }) => {
      if (!prop) return
      let json = {}
      let mapParam = {}
      if (isDynamic) {
        let checkDup = allFormGroup.find(({ key }) => key === systemKey)
        if (!checkDup) {
          system.forEach(({ field, systemKey }) => {
            objectPath.set(json, field, [])
            objectPath.set(mapParam, systemKey, field)
          })
          allFormGroup.push({
            key: fullPropKey || systemKey,
            formDataName: systemName,
            json: json,
            mapParam: mapParam,
            isSystem: false,
            isDynamic: isDynamic,
            dynamicName: systemName,
            elementKey: elementKey,
            componentName: systemName,
          })
        }
      } else {
        system.forEach(({ field, systemKey }) => {
          objectPath.set(json, field, '')
          objectPath.set(mapParam, systemKey, field)
        })
        allFormGroup.push({
          key: fullPropKey || systemKey,
          formDataName: systemName,
          json: json,
          mapParam: mapParam,
          isSystem: false,
          elementKey: elementKey,
          componentName: systemName,
        })
      }
    },
  )
  let mapTag = jsonForm?.schema?.mapTag
  let widgetSchema = Object.keys(mapTag).filter(
    (key) => mapTag[key] === 'widget',
  )
  widgetSchema.forEach((key) => {
    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
    if (widgetDesign) {
      let widgetCode = ui?.widget?.formDesign?.code
      if (widgetCode) {
        let design = objectPath.get(widgetDesign, widgetCode)
        if (design) {
          let { widgetField } = design
          let isGroupValue = ui.isGroupValue
          let uiName = ui.name
          allFormGroup.push({
            key: ui.key,
            formDataName: ui.fieldName,
            isSystem: false,
            isWidget: true,
            isGroup: isGroupValue,
            groupName: isGroupValue ? uiName : '',
            widgetField,
          })
        }
      }
    }
  })
  return allFormGroup
}

export const createWidgetField = (jsonForm) => {
  let mapFormData = jsonForm?.schema?.mapFormData || {}
  let widgetField = []
  Object.keys(mapFormData).forEach((key) => {
    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
    widgetField.push({
      name: ui.name,
      fieldName: ui.fieldName,
    })
  })
  return widgetField
}

export const createWidgetFormGroup = (jsonForm, widgetData, ui) => {
  let formGroup = jsonForm?.formGroup || []
  let widgetJsonForm = widgetData?.jsonForm
  let widgetFormGroup = widgetJsonForm.formGroup || []
  let isGroupValue = ui.isGroupValue
  let groupName = isGroupValue ? ui.name : ''

  if (widgetFormGroup.length > 0) {
    widgetFormGroup.forEach((form) => {
      if (!form) return
      let key = `${ui.key}-${form.key}`
      let foundIndex = formGroup.findIndex(
        (item) => item.key === key || item.key === ui.key,
      )
      let newForm = {
        key,
        isSystem: true,
        isWidget: true,
        widgetCode: jsonForm.formCode,
        widgetKey: ui.key,
        widgetName: ui.name,
        isGroup: isGroupValue,
        groupName: groupName,
        formDataName: `${ui.fieldName} | ${form.formDataName}`,
        mapParam: form.mapParam,
        mapOther: form.mapOther,
        json: form.json,
      }
      if (foundIndex === -1) {
        formGroup.push(newForm)
      } else {
        formGroup[foundIndex] = newForm
      }
    })
  } else {
    let defaultFormGroupIndex = formGroup.findIndex(
      (item) => item.key === ui.key,
    )
    if (defaultFormGroupIndex === -1) {
      formGroup.push({
        key: ui.key,
        formDataName: ui.fieldName,
        isSystem: true,
        isWidget: true,
        isGroup: isGroupValue,
        groupName: isGroupValue ? ui.name : '',
        widgetCode: jsonForm.formCode,
        widgetKey: ui.key,
        widgetName: ui.name,
        isGroup: isGroupValue,
        groupName: groupName,
        widgetField: createWidgetField(widgetJsonForm),
      })
    } else {
      let defaultFormGroup = formGroup[defaultFormGroupIndex]
      defaultFormGroup = {
        ...defaultFormGroup,
        key: ui.key,
        formDataName: ui.fieldName,
        isSystem: true,
        isWidget: true,
        isGroup: isGroupValue,
        groupName: isGroupValue ? ui.name : '',
        widgetCode: jsonForm.formCode,
        widgetKey: ui.key,
        widgetName: ui.name,
        isGroup: isGroupValue,
        groupName: groupName,
        widgetField: createWidgetField(widgetJsonForm),
      }
      formGroup[defaultFormGroupIndex] = defaultFormGroup
    }
  }
  return formGroup
}

export const isNumber = (a) => Number(a) === +a

export const isFile = (file) => 'File' in window && file instanceof File

export const getCheckValue = (value, format, isUseNull, isSelectCompare) => {
  if (value === 'true') {
    value = true
  } else if (value === 'false') {
    value = false
  } else if (value === 'null') {
    value = null
  } else if (value === 'undefined') {
    value = undefined
  }
  if (
    value === true ||
    value === false ||
    value === null ||
    value === undefined
  ) {
    return value
  }
  if (value === '') {
    return isUseNull ? null : value
  }
  if (format) {
    if (format === 'number' || format === 'numeric') {
      return Number(value)
    }
    return value
  }
  if (isSelectCompare) {
    if (isNumber(value)) {
      return value.toString()
    }
  }
  if (isNumber(value)) {
    return Number(value)
  }
  return value
}

export const compareTwoValue = (
  compareType,
  valueA,
  valueB,
  isSelectCompare = false,
) => {
  valueA = getCheckValue(valueA, null, null, isSelectCompare)
  valueB = getCheckValue(valueB, null, null, isSelectCompare)
  if (compareType === 'equalValue') {
    if (isEmpty(valueA) && isEmpty(valueB)) {
      return isEmpty(valueA) === isEmpty(valueB)
    }
    return valueA === valueB
  }
  if (compareType === 'notEqualValue') {
    if (isEmpty(valueA) && isEmpty(valueB)) {
      return isEmpty(valueA) !== isEmpty(valueB)
    }
    return valueA !== valueB
  }
  if (compareType === 'hasValue') return !isEmpty(valueA)
  if (compareType === 'emptyValue') return isEmpty(valueA)
  if (compareType === 'includeValue') return isInclude(valueA, valueB)
  if (compareType === 'excludeValue') return isExclude(valueA, valueB)
}

export const checkIsEqual = (compareValue, value) => {
  return getCheckValue(compareValue) === getCheckValue(value)
}

export const checkIsNotEqual = (compareValue, value) => {
  return getCheckValue(compareValue) !== getCheckValue(value)
}

export const setRequestFormData = (
  form = {},
  requestBody = {},
  request = {},
) => {
  let { jsonKey, mapParam = {}, mapOther = {}, isSystem, componentName } = form
  let {
    datasourceValue = {},
    formData = {},
    allParams = {},
    jsonForm = {},
    rowData = {},
  } = request
  let mapTag = objectPath.get(jsonForm, 'schema.mapTag') || []
  let isComponentData = !isSystem
  let paramList = Object.keys(mapParam)
  let otherList = Object.values(mapOther)

  paramList.forEach((key) => {
    let name = mapParam[key]
    let dataKey
    let requestKey
    if (isComponentData && componentName) {
      dataKey = componentName
      requestKey = jsonKey ? `${jsonKey}.${componentName}` : componentName
    } else {
      dataKey = key
      requestKey = jsonKey ? `${jsonKey}.${name}` : name
    }
    let value
    if (isComponentData) {
      value = objectPath.get(formData, componentName)
    } else if (objectPath.has(rowData, name)) {
      value = objectPath.get(rowData, name)
    } else {
      value =
        objectPath.get(formData, dataKey) || objectPath.get(formData, name)
    }

    if (value === 'true') {
      objectPath.set(requestBody, requestKey, true)
    } else if (value === 'false') {
      objectPath.set(requestBody, requestKey, false)
    } else if (value === 'null') {
      objectPath.set(requestBody, requestKey, null)
    } else {
      let isTable = objectPath.get(mapTag, [key]) === 'table'
      if (isTable && value) {
        value.map((item) => {
          objectPath.del(item, 'tableData')
          return item
        })
      }
      objectPath.set(requestBody, requestKey, value)
    }
  })

  otherList.forEach(({ name, value, dataType = '', jsonKey }) => {
    let dataKey = jsonKey ? `${jsonKey}.${name}` : name
    if (dataType === 'prop' || dataType === 'system') {
      let value = objectPath.get(formData, dataKey)
      objectPath.set(requestBody, name, value)
    } else if (dataType === 'urlParam') {
      let urlParamValue = getUrlParamValue(allParams, value, dataKey)
      objectPath.set(requestBody, name, urlParamValue)
    } else if (dataType === 'custom') {
      if (isNumber(value)) {
        objectPath.set(requestBody, name, Number(value))
      } else if (value === 'true') {
        objectPath.set(requestBody, name, true)
      } else if (value === 'false') {
        objectPath.set(requestBody, name, false)
      } else if (value === 'null') {
        objectPath.set(requestBody, name, null)
      } else {
        objectPath.set(requestBody, name, value)
      }
    } else if (dataType === 'dateTime') {
      let now = null
      if (value === 'currentDate') {
        now = moment().format('YYYY-MM-DD')
      } else if (value === 'currentTime') {
        now = moment().format('HH:mm:ss')
      } else if (value === 'currentDateTime') {
        now = moment().format('YYYY-MM-DD HH:mm:ss')
      }
      objectPath.set(requestBody, name, now)
    } else if (dataType === 'datasource') {
      let dataValue = objectPath.get(datasourceValue, value)
      objectPath.set(requestBody, name, dataValue)
    }
  })
  return requestBody
}

export const setRowDataToFormData = (form, request) => {
  let { formComponentName, jsonKey = '', mapParam = {}, mapArray } = form
  let { formData = {}, jsonForm = {}, rowData = {}, isUiChanged } = request
  let mapTag = objectPath.get(jsonForm, 'schema.mapTag') || []
  let paramList = Object.keys(mapParam)
  paramList.forEach((key) => {
    let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema, isUiChanged)
    let name = mapParam[key]
    let rowDataKey = jsonKey ? `${jsonKey}.${name}` : name
    let dataKey = formComponentName ? `${formComponentName}.${key}` : key
    let dataName = formComponentName ? `${formComponentName}.${name}` : name
    let value
    if (objectPath.has(rowData, rowDataKey)) {
      value = objectPath.get(rowData, rowDataKey)
    }
    if (value === 'true') {
      value = true
    } else if (value === 'false') {
      value = false
    } else if (value === 'null') {
      value = null
    } else {
      let isTable = objectPath.get(mapTag, [key]) === 'table'
      if (isTable && value) {
        value.map((item) => {
          objectPath.del(item, 'tableData')
          return item
        })
      }
    }
    if (ui?.tagname === 'select') {
      let { isMulti } = ui
      if (isMulti) {
        value = [value]
      }
    }
    objectPath.set(formData, dataKey, value)
    objectPath.set(formData, dataName, value)
  })
  return formData
}

export const setRowDataToBulkForm = (bulkForm, request) => {
  let { mainBulk, bulkTable } = bulkForm
  let { formData = {}, jsonForm = {}, rowData = {} } = request

  let mainBulkValue = objectPath.get(rowData, mainBulk.name)
  if (!mainBulkValue) return
  let mainBulkUi = getCurrentUi(
    mainBulk.key,
    jsonForm.schema,
    jsonForm.uischema,
  )
  let { tagname, isMulti } = mainBulkUi
  if (tagname === 'select' && isMulti) {
    mainBulkValue = [mainBulkValue]
  }
  objectPath.set(formData, mainBulk.key, mainBulkValue)
  objectPath.set(formData, mainBulk.name, mainBulkValue)

  mainBulkValue.forEach((mainValue) => {
    bulkTable.forEach((item) => {
      let { isArray, isParent, key, name, bulkChild } = item
      let ui = getCurrentUi(key, jsonForm.schema, jsonForm.uischema)
      let value
      if (objectPath.has(rowData, name)) {
        value = objectPath.get(rowData, name)
      }
      if (ui.tagname === 'select') {
        let { isMulti } = ui
        if (isMulti) {
          value = [value]
        }
      }
      if (isParent) {
        objectPath.set(formData, key, value)
        objectPath.set(formData, name, value)
      } else {
        if (isArray) {
          let parentElements = ui?.elements || []
          parentElements.forEach((parentItem) => {
            let subElements = parentItem?.elements || []
            bulkChild.forEach((childItem) => {
              let childKey = childItem.value
              let foundChild = subElements.find(({ key }) => key === childKey)

              let childUi = getCurrentUi(
                childKey,
                jsonForm.schema,
                jsonForm.uischema,
              )
              let fullName = `@${parentItem.name}.${childUi.name}`
              let rowValue
              if (parentItem.optionId === mainValue) {
                rowValue = objectPath.get(rowData, childUi.name) || ''
              } else {
                rowValue = ''
              }
              objectPath.set(formData, fullName, rowValue)
              if (childUi.tagname === 'checkboxGroup') {
                let splitValue = rowValue.split(childUi?.joinValue || '')
                let checkboxOptions = foundChild?.options || []
                let isCheckAll = splitValue.length === checkboxOptions.length
                objectPath.set(
                  formData,
                  `@isCheckedAll.${fullName}`,
                  isCheckAll,
                )
              }
            })
          })
        }
      }
    })
  })
  return formData
}

export const getDateTimeValue = (dataValue, item, prop) => {
  const { formatDate, formatTime, formatDateTime } = prop
  let { dateTimeType, format, custom = {} } = item
  if (format === 'system') {
    if (dateTimeType === 'date') {
      dataValue = formatDate(dataValue)
    }
    if (dateTimeType === 'time') {
      dataValue = formatTime(dataValue)
    }
    if (dateTimeType === 'dateTime') {
      dataValue = formatDateTime(dataValue)
    }
  } else if (format === 'custom') {
    let { dateFormat, timeFormat } = custom
    if (dateTimeType === 'date') {
      dataValue = formatDate(dataValue, dateFormat)
    }
    if (dateTimeType === 'time') {
      dataValue = formatTime(dataValue, timeFormat)
    }
    if (dateTimeType === 'dateTime') {
      dataValue = formatDateTime(dataValue, `${dateFormat} ${timeFormat}`)
    }
  }
  return dataValue
}

export const getDateAge = (dataValue, item, props) => {
  let { isMulti = false, dateLimit = null } = props || {}
  let { dateAgeType, includeLastDay } = item
  let dateCal
  let labelName = { year: 'ปี', month: 'เดือน', day: 'วัน' }
  let valueKey = { year: 'ageYear', month: 'ageMonth', day: 'ageDay' }
  let labelItems = []
  let dateString = []
  let ageList = {}

  if (dateAgeType === 'd') {
    dateCal = calDay(dataValue, dateLimit)
    if (includeLastDay) {
      dateCal = {
        ...dateCal,
        day: dateCal.day + 1,
      }
    }
    labelItems = ['day']
  } else if (dateAgeType === 'm') {
    dateCal = calMonth(dataValue, dateLimit)
    labelItems = ['month']
  } else {
    dateCal = calDate(dataValue, dateLimit)
  }

  if (dateAgeType === 'ymd') {
    labelItems = ['year', 'month', 'day']
  }
  if (dateAgeType === 'ym') {
    labelItems = ['year', 'month']
  }
  if (dateAgeType === 'y') {
    labelItems = ['year']
  }
  labelItems.forEach((key) => {
    let dateValue = objectPath.get(dateCal, key)
    let suffix = objectPath.get(labelName, key)
    dateString.push(dateValue)
    dateString.push(suffix)
    let ageKey = objectPath.get(valueKey, key)
    objectPath.set(ageList, ageKey, dateValue)
  })
  if (isMulti) {
    return ageList
  }
  dateString = dateString.join(' ')
  return dateString
}

export const getDatasourceValue = (prop) => {
  let {
    item = {},
    key,
    datasourceValue = {},
    formValue,
    isGroup,
    formatDate,
    formatTime,
    formatDateTime,
  } = prop

  let {
    type = 'string',
    isReceiveProp,
    sourceKey,
    pattern,
    patternColor,
    replaceValue,
  } = item

  let dataValue = ''
  let empty = false
  if (isReceiveProp) {
    let receiveData = objectPath.get(datasourceValue, 'receiveProps') || {}
    if (objectPath.has(receiveData, key)) {
      dataValue = objectPath.get(receiveData, key)
    } else {
      dataValue = objectPath.get(receiveData, item.value)
    }
  } else if (item.dataType === 'dataType') {
    dataValue = objectPath.get(
      datasourceValue,
      `${item.sourceKey}.${item.value}`,
    )
  } else if (key) {
    dataValue = objectPath.get(datasourceValue, key)
  } else if (sourceKey) {
    dataValue = objectPath.get(datasourceValue, sourceKey)
  } else if (item.dataType === 'input') {
    dataValue = formValue
  } else if (item.dataType === 'today') {
    dataValue = moment().format('YYYY-MM-DD HH:mm:ss')
  }
  if (dataValue === '' || dataValue === null || dataValue === undefined) {
    empty = true
  }
  if (type === 'string') {
    dataValue = empty ? (isGroup ? '' : '-') : dataValue
  } else if (type === 'dateTime') {
    dataValue = empty
      ? isGroup
        ? ''
        : '-'
      : getDateTimeValue(dataValue, item, prop)
  } else if (type === 'dateAge') {
    dataValue = empty ? (isGroup ? '' : '-') : getDateAge(dataValue, item)
  } else if (type === 'list') {
    let dataArray = []
    if (!dataValue) return ''
    if (Array.isArray(dataValue)) {
      dataArray = dataValue.filter((item) => {
        return !objectPath.has(item, '@isDeleted')
      })
    } else if (typeof dataValue === 'object') {
      dataArray = Object.keys(dataValue) || []
    } else {
      dataArray = []
    }
    if (dataArray?.length === 0) return ''
    let replaceOption = {
      replace: (replaceItem) => {
        let { name, children } = replaceItem
        if (name === 'code' && children && children[0]?.data) {
          return (
            <Row>
              {dataArray.map((value, index) => {
                let checkValue = cloneDeep(value)
                objectPath.del(checkValue, 'tableData')
                objectPath.del(checkValue, '@genId')
                objectPath.del(checkValue, '@seq')
                if (
                  isEmpty(checkValue) ||
                  objectPath.get(checkValue, '@isDeleted')
                )
                  return ''

                if (item.subPattern) {
                  let replaceOption = {
                    replace: (replaceItem) => {
                      let { name, children } = replaceItem
                      if (name === 'code' && children && children[0]?.data) {
                        let itemValue
                        let key = children[0]?.data
                        if (key === '@numberOrder' || key === '@no') {
                          itemValue = index + 1
                        } else {
                          let name = key.replace('@', '')
                          name = name.replace('@', '')
                          let isDate = /\[date\]/.test(name)
                          let isTime = /\[time\]/.test(name)
                          let isDateTime = /\[dateTime\]/.test(name)
                          if (isDate) {
                            name = name.replace('[date]', '')
                            itemValue = objectPath.get(value, name)
                            itemValue = formatDate(itemValue)
                          } else if (isTime) {
                            name = name.replace('[time]', '')
                            itemValue = objectPath.get(value, name)
                            itemValue = formatTime(itemValue)
                          } else if (isDateTime) {
                            name = name.replace('[dateTime]', '')
                            itemValue = objectPath.get(value, name)
                            itemValue = formatDateTime(itemValue)
                          } else {
                            itemValue = objectPath.get(value, name)
                          }
                        }
                        if (patternColor) {
                          let foundPatternColor = patternColor.find(
                            ({ field }) => field === key,
                          )
                          if (foundPatternColor) {
                            let { textColor } = foundPatternColor
                            let color
                            if (textColor.startsWith('@')) {
                              let textColorKey = textColor.replace('@', '')
                              color = objectPath.get(value, textColorKey)
                            } else {
                              color = textColor
                            }
                            if (color) {
                              return <span style={{ color }}>{itemValue}</span>
                            }
                          }
                        }
                        return <>{itemValue}</>
                      }
                    },
                  }

                  let parseValue = parse(item.subPattern, replaceOption)
                  return (
                    <Col sm="12" className="code-value">
                      {parseValue}
                    </Col>
                  )
                }
                return ''
              })}
            </Row>
          )
        }
      },
    }
    let parseValue = parse(pattern, replaceOption)
    return <label className="code-value">{parseValue}</label>
  }
  if (Array.isArray(dataValue)) {
    return JSON.stringify(dataValue)
  }
  if (typeof dataValue === 'object') {
    return JSON.stringify(dataValue)
  }
  if (replaceValue) {
    replaceValue.forEach(({ sourceValue, replaceValue }) => {
      let isEqual = compareTwoValue('equalValue', sourceValue, dataValue)
      if (isEqual) {
        dataValue = replaceValue
      }
    })
  }
  return dataValue
}

const findUiSchema = (elements, indices) => {
  let newElements = indices.reduce(
    (o, i) => objectPath.get(o, ['elements', i]),
    { elements },
  )
  return newElements
}

const findUischemaIndex = (currentKey, mapKey, elements) => {
  let indexList = []
  let newElements = elements
  if (mapKey) {
    let listKey = mapKey.split('.')
    listKey.forEach((key, i, arr) => {
      let index = newElements.findIndex((element) => element.key === key)
      let element = objectPath.get(newElements, [index, 'elements'])
      if (element) {
        newElements = element
        indexList = [...indexList, index]
      }
      if (key === currentKey) {
        arr.length = i + 1
      }
    })
    return indexList
  }
  return []
}

const getUi = (key, schema, uischema) => {
  let mapKey = objectPath.get(schema, ['mapKey', key]) || ''
  let elements = uischema.elements
  let indices = findUischemaIndex(key, mapKey, elements)
  let currentElement = findUiSchema(elements, indices)
  return currentElement
}

export const getCurrentUi = (key, schema, uischema, isUiChanged) => {
  if (isUiChanged) {
    let tabForm = isUiChanged?.tabForm
    if (tabForm) {
      let tabFormUi
      if (tabForm) {
        Object.values(tabForm).forEach(({ jsonForm }) => {
          let ui = getUi(key, jsonForm.schema, jsonForm.uischema)
          if (ui.key === key) {
            tabFormUi = ui
          }
        })
      }
      if (tabFormUi) return tabFormUi
    }
  }
  return getUi(key, schema, uischema)
}

export const updateUiSchema = (path, key, value, jsonForm) => {
  if (!jsonForm.schema.mapKey) return
  let elements = jsonForm.uischema.elements
  let mapKey = jsonForm.schema.mapKey[path]
  let indices = findUischemaIndex(key, mapKey, elements)
  if (!indices) return
  let newElement = indices.reduce((o, i) => o.elements[i], { elements })
  objectPath.set(newElement, key, value)
  let newUiSchema = jsonForm.uischema
  newUiSchema.elements = elements
  return newUiSchema
}

export const getAllKeys = (obj, path = [], results = []) => {
  if (obj) {
    const keys = Object.keys(obj)
    if (keys) {
      keys.forEach((key) => {
        const currentPath = path.concat([key])
        results.push(currentPath.join('.'))
        let value = objectPath.get(obj, key)
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          getAllKeys(value, currentPath, results)
        }
      })
    }
  }
  return results
}

const getLabelValue = (titleValue, mapOther, rowData) => {
  let titleList = []
  let foundValue = true
  titleValue.forEach(({ title }) => {
    title.forEach((item) => {
      let isNew = objectPath.get(item, '__isNew__')
      let key = objectPath.get(item, 'value')
      if (isNew) {
        if (objectPath.has(rowData, key)) {
          let itemValue = objectPath.get(rowData, key)
          titleList.push(itemValue)
        } else {
          titleList.push(key)
        }
      } else {
        let itemValue
        if (objectPath.has(rowData, key)) {
          itemValue = objectPath.get(rowData, key)
        } else {
          if (mapOther) {
            let foundColumn = mapOther.find((other) => other.value === key)
            if (foundColumn) {
              let key = foundColumn.field
              itemValue = objectPath.get(rowData, key)
            }
          }
        }
        titleList.push(itemValue)
        if (isEmpty(itemValue)) {
          foundValue = false
        }
      }
    })
  })
  return foundValue ? titleList.join('') : ''
}

export const getTitleValue = (column, rowData) => {
  let { optionType, mapOther } = column
  let optionData = objectPath.get(column, `${optionType}`) || {}
  let titleValue = objectPath.get(optionData, 'titleValue') || []
  return getLabelValue(titleValue, mapOther, rowData)
}

export const getFieldValue = (column, rowData) => {
  let { optionType, mapOther } = column
  let optionData = objectPath.get(column, `${optionType}`) || {}
  let titleValue = objectPath.get(optionData, 'fieldTitle.titleValue') || []
  return getLabelValue(titleValue, mapOther, rowData)
}

export const getRowFieldValue = (column, rowData) => {
  let { optionType, mapOther } = column
  let optionData = objectPath.get(column, `${optionType}`) || {}
  let { isUseLabelPattern = true } = objectPath.get(column, `${optionType}`)
  let titleValue = objectPath.get(optionData, 'rowDataField.titleValue')
  if (titleValue) {
    return getLabelValue(titleValue, mapOther, rowData)
  }
  if (isUseLabelPattern) {
    return getTitleValue(column, rowData)
  }
  return getFieldValue(column, rowData)
}

export const getFieldName = (fieldName, { config, widgetName }) => {
  const {
    isDynamic = false,
    dynamicName = '',
    dynamicIndex = 0,
    isArray,
    arrayName,
  } = config

  let prefix
  if (isArray) {
    prefix = `@${arrayName}`
  } else if (isDynamic) {
    prefix = `${dynamicName}.${dynamicIndex}`
  }
  if (widgetName) {
    if (prefix) {
      prefix = `${widgetName}.${prefix}`
    } else {
      prefix = widgetName
    }
  }
  let field = prefix ? `${prefix}.${fieldName}` : fieldName
  return field.replace('.', '-')
}

export const applyMask = (value, mask) => {
  if (!value) return value
  if (!mask) return value
  const result = []
  let valueIndex = 0
  for (let i = 0; i < mask.length; i++) {
    const maskChar = mask[i]
    const currentChar = value[valueIndex]
    if (!currentChar) break
    const isDigit = /\d/.test(currentChar)
    const isAlpha = /[A-Za-z]/.test(currentChar)
    const isAlphanumeric = /[A-Za-z0-9]/.test(currentChar)
    if (maskChar === '9') {
      if (isDigit) {
        result.push(currentChar)
        valueIndex++
      } else {
        // Skip invalid input char
        valueIndex++
        i--
      }
    } else if (maskChar === 'a') {
      if (isAlpha) {
        result.push(currentChar)
        valueIndex++
      } else {
        valueIndex++
        i--
      }
    } else if (maskChar === '*') {
      if (isAlphanumeric) {
        result.push(currentChar)
        valueIndex++
      } else {
        valueIndex++
        i--
      }
    } else {
      // Fixed mask character (like '-' or ':')
      result.push(maskChar)
    }
  }
  return result.join('')
}

export const toMetric = (number) => {
  if (!number) return ''
  const abs = Math.abs(number)
  const units = [
    { symbol: 'T', value: 1e12 },
    { symbol: 'G', value: 1e9 },
    { symbol: 'M', value: 1e6 },
    { symbol: 'K', value: 1e3 },
    { symbol: '', value: 1 },
    { symbol: 'm', value: 1e-3 },
    { symbol: 'μ', value: 1e-6 },
    { symbol: 'n', value: 1e-9 },
    { symbol: 'p', value: 1e-12 },
  ]
  for (let unit of units) {
    if (abs >= unit.value) {
      return (
        (number / unit.value).toFixed(1).replace(/\.?0+$/, '') + unit.symbol
      )
    }
  }
  return number.toString()
}

export const formatNumber = (num, decimals = 2) => {
  return num.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

export const truncateDecimal = (value, precision = 0, isFixed = false) => {
  if (!precision || !value) return value
  const num = Number(value)
  if (isNaN(num)) return value
  const factor = Math.pow(10, precision)
  const truncated = Math.trunc(num * factor) / factor
  return isFixed ? truncated.toFixed(precision) : truncated
}
